import Axios from 'axios';
import { cacheGet, cacheSet } from './utils/cache';

const path = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const axios = Axios.create({
  baseURL: path,
  headers: {},
});

// --- Global concurrency limiter --------------------------------------------------------------
const MAX_CONCURRENT = 3;
let activeRequests = 0;
const waiters: Array<() => void> = [];

const acquireSlot = () =>
  new Promise<void>((resolve) => {
    if (activeRequests < MAX_CONCURRENT) {
      activeRequests++;
      resolve();
    } else {
      waiters.push(() => {
        activeRequests++;
        resolve();
      });
    }
  });

const releaseSlot = () => {
  activeRequests = Math.max(0, activeRequests - 1);
  waiters.shift()?.();
};

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CACHEABLE_PATH = /^\/(artists|albums|tracks)(\/|$)/;

const isCacheableGet = (config: any) =>
  (config.method || 'get').toLowerCase() === 'get' && CACHEABLE_PATH.test(config.url || '');

const cacheKeyFor = (config: any) =>
  `${config.url}?${JSON.stringify(config.params || {})}`;

axios.interceptors.request.use(async (config) => {
  await acquireSlot();

  if (isCacheableGet(config)) {
    const key = cacheKeyFor(config);
    const entry = await cacheGet(key);
    if (entry && entry.expiry > Date.now()) {
      (config as any).adapter = async () => ({
        data: entry.data,
        status: 200,
        statusText: 'OK (cache)',
        headers: {},
        config,
        request: {},
      });
    } else {
      (config as any).__cacheKey = key;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    releaseSlot();

    const key = (response.config as any).__cacheKey;
    if (key && response.status === 200) {
      void cacheSet(key, { data: response.data, expiry: Date.now() + CACHE_TTL_MS });
    }
    return response;
  },
  async (error) => {
    releaseSlot();
    return Promise.reject(error);
  }
);

export default axios;
